import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  Clock, 
  CheckCircle, 
  Users,
  Check,
  X,
  RefreshCw,
  Plus,
  Download,
  Upload,
  Edit,
  Save,
  Trash2
} from "lucide-react";

const HoursApprovalPage = () => {
  const { token, hasRole, user } = useAuth();
  const [pendingHours, setPendingHours] = useState([]);
  const [allHoursSummary, setAllHoursSummary] = useState([]);
  const [allMembers, setAllMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [approving, setApproving] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [editingHoursId, setEditingHoursId] = useState(null);
  const [editHoursValue, setEditHoursValue] = useState(20);
  const [editCompletedHoursId, setEditCompletedHoursId] = useState(null);
  const [editCompletedHoursValue, setEditCompletedHoursValue] = useState(0);
  const [diaryEntries, setDiaryEntries] = useState([]);
  const [diaryFilterUser, setDiaryFilterUser] = useState("all");
  const [diarySearch, setDiarySearch] = useState("");
  const [editEntry, setEditEntry] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [bulkForm, setBulkForm] = useState({
    datum: new Date().toISOString().split('T')[0],
    ure: 4,
    tip: "delovna_akcija",
    opis: "",
    orodje: "brez",
    potrjeno: true
  });

  const orodjaOptions = [
    { value: "brez", label: "Brez orodja (ročno delo)", faktor: 1 },
    { value: "motorna_zaga", label: "Motorna žaga", faktor: 2 },
    { value: "kosilnica", label: "Kosilnica", faktor: 2 },
    { value: "traktor", label: "Traktor", faktor: 3 },
    { value: "delovni_stroj", label: "Delovni stroj", faktor: 3 },
  ];

  const getOrodjeFaktor = (orodje) => {
    const o = orodjaOptions.find(x => x.value === orodje);
    return o ? o.faktor : 1;
  };

  const getOrodjeLabel = (orodje) => {
    const o = orodjaOptions.find(x => x.value === orodje);
    return o ? o.label : "Brez orodja";
  };

  const isAdmin = hasRole(["administrator", "predsednik", "gospodar"]);
  const canViewAllHours = hasRole(["administrator", "predsednik", "gospodar", "upravnik_lovske_koce", "skupinovodja"]);

  const fetchDiary = async (filterUserId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const params = filterUserId && filterUserId !== "all" ? `?user_id=${filterUserId}` : "";
      const res = await axios.get(`${API}/hours/dnevnik${params}`, { headers });
      setDiaryEntries(res.data);
    } catch (error) {
      console.error("Error fetching diary:", error);
    }
  };

  useEffect(() => {
    fetchData();
    fetchDiary(canViewAllHours ? "all" : null);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      // Use allSettled so one 403 (e.g. summary/all for upravnik) doesn't break the whole page
      const [pendingRes, summaryRes, membersRes] = await Promise.allSettled([
        axios.get(`${API}/hours/pending`, { headers }),
        axios.get(`${API}/hours/summary/all`, { headers }),
        axios.get(`${API}/users`, { headers })
      ]);
      if (pendingRes.status === "fulfilled") {
        setPendingHours(pendingRes.value.data);
      } else {
        console.warn("[HoursApproval] pending fetch failed:", pendingRes.reason?.response?.status);
      }
      if (summaryRes.status === "fulfilled") {
        setAllHoursSummary(summaryRes.value.data);
      } else {
        console.warn("[HoursApproval] summary fetch failed:", summaryRes.reason?.response?.status);
        setAllHoursSummary([]);
      }
      if (membersRes.status === "fulfilled") {
        setAllMembers(membersRes.value.data || []);
      } else {
        console.warn("[HoursApproval] users fetch failed:", membersRes.reason?.response?.status);
        setAllMembers([]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleMemberSelect = (id) => {
    setSelectedMembers(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const selectAll = () => {
    if (selectedIds.length === pendingHours.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(pendingHours.map(h => h.id));
    }
  };

  const selectAllMembers = () => {
    if (selectedMembers.length === allMembers.length) {
      setSelectedMembers([]);
    } else {
      setSelectedMembers(allMembers.map(m => m.id));
    }
  };

  const handleBulkApprove = async () => {
    if (selectedIds.length === 0) {
      toast.error("Izberite vsaj en zapis");
      return;
    }

    setApproving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(`${API}/hours/approve-bulk`, {
        hours_ids: selectedIds
      }, { headers });
      
      toast.success(response.data.message);
      setSelectedIds([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Napaka pri potrjevanju");
    } finally {
      setApproving(false);
    }
  };

  const handleBulkAddHours = async () => {
    if (selectedMembers.length === 0) {
      toast.error("Izberite vsaj enega člana");
      return;
    }
    if (!bulkForm.opis.trim()) {
      toast.error("Vnesite opis");
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(`${API}/hours/bulk-add`, {
        user_ids: selectedMembers,
        ...bulkForm
      }, { headers });
      
      toast.success(response.data.message);
      setBulkDialogOpen(false);
      setSelectedMembers([]);
      setBulkForm({
        datum: new Date().toISOString().split('T')[0],
        ure: 4,
        tip: "delovna_akcija",
        opis: "",
        orodje: "brez",
        potrjeno: true
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Napaka pri dodajanju");
    }
  };

  const handleSingleApprove = async (id) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/hours/${id}/approve`, {}, { headers });
      toast.success("Ure potrjene");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Napaka pri potrjevanju");
    }
  };

  const handleReject = async (id) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/hours/${id}/reject`, {}, { headers });
      toast.success("Zapis zavrnjen");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Napaka pri zavrnitvi");
    }
  };

  const handleDeleteHours = async (id) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/hours/${id}`, { headers });
      toast.success("Ure izbrisane");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Napaka pri brisanju");
    }
  };

  const handleExportHours = async () => {
    try {
      const res = await fetch(`${API}/download/hours-csv?token=${token}`);
      if (!res.ok) throw new Error('Napaka');
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = 'ure_ld_rakek.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
      toast.success("CSV izvožen");
    } catch (error) {
      toast.error("Napaka pri izvozu");
    }
  };

  const handleUpdateMandatoryHours = async (memberId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/users/${memberId}/mandatory-hours`, { 
        obvezne_ure: editHoursValue 
      }, { headers });
      toast.success("Obvezne ure posodobljene");
      setEditingHoursId(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Napaka pri posodabljanju");
    }
  };

  const startEditHours = (memberId, currentHours) => {
    setEditingHoursId(memberId);
    setEditHoursValue(currentHours || 20);
  };

  const handleUpdateCompletedHours = async (memberId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/users/${memberId}/completed-hours`, { 
        opravljene_ure: editCompletedHoursValue 
      }, { headers });
      toast.success("Opravljene ure posodobljene - podatki osveženi");
      setEditCompletedHoursId(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Napaka pri posodabljanju");
    }
  };

  const startEditCompletedHours = (memberId, currentHours) => {
    setEditCompletedHoursId(memberId);
    setEditCompletedHoursValue(currentHours || 0);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("sl-SI", { day: "numeric", month: "short", year: "numeric" });
  };

  const getHoursTypeLabel = (tip) => {
    const labels = {
      delovna_akcija: "Delovna akcija",
      skupni_lov: "Skupni lov",
      podarjene_ure: "Podarjene ure",
      drugo: "Drugo"
    };
    return labels[tip] || tip;
  };

  const getHoursTypeColor = (tip) => {
    const colors = {
      delovna_akcija: "bg-orange-100 text-orange-800",
      skupni_lov: "bg-green-100 text-green-800",
      podarjene_ure: "bg-pink-100 text-pink-800",
      drugo: "bg-gray-100 text-gray-800"
    };
    return colors[tip] || "bg-gray-100 text-gray-800";
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm("Ali ste prepričani, da želite izbrisati ta zapis?")) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/hours/${entryId}`, { headers });
      toast.success("Zapis izbrisan");
      fetchDiary(diaryFilterUser);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Napaka pri brisanju");
    }
  };

  const startEditEntry = (entry) => {
    setEditEntry(entry.id);
    setEditForm({
      ure: entry.dejanske_ure || entry.ure,
      orodje: entry.orodje || "brez",
      tip: entry.tip,
      opis: entry.opis,
      datum: entry.datum
    });
  };

  const handleSaveEntry = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
      await axios.put(`${API}/hours/${editEntry}`, editForm, { headers });
      toast.success("Zapis posodobljen");
      setEditEntry(null);
      fetchDiary(diaryFilterUser);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Napaka pri shranjevanju");
    }
  };

  // Group pending hours by user
  const groupedByUser = pendingHours.reduce((acc, entry) => {
    if (!acc[entry.user_id]) {
      acc[entry.user_id] = { user_ime: entry.user_ime, entries: [] };
    }
    acc[entry.user_id].entries.push(entry);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in" data-testid="hours-approval-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-primary flex items-center gap-2">
            <Clock className="h-6 w-6 sm:h-8 sm:w-8" />
            Delovne ure
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Potrjevanje in dodajanje ur članov
          </p>
        </div>
        
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              if (!window.confirm("Nastavim izračunane obvezne ure (po Poslovniku) vsem članom? Opravljene ure ostanejo nespremenjene.")) return;
              try {
                const r = await axios.post(`${API}/hours/apply-calculated`, {}, { headers: { Authorization: `Bearer ${token}` } });
                toast.success(r.data.message);
                fetchData();
              } catch (e) {
                toast.error(e.response?.data?.detail || "Napaka");
              }
            }}
            data-testid="apply-calculated-hours-btn"
          >
            <Clock className="h-4 w-4 mr-1" />
            Uporabi izračunane ure
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportHours}>
            <Download className="h-4 w-4 mr-1" />
            Izvozi
          </Button>
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-1" />
            Osveži
          </Button>
        </div>
      </div>

      <Tabs defaultValue="dnevnik" className="w-full">
        <TabsList className="grid w-full grid-cols-4 h-auto">
          <TabsTrigger value="dnevnik" className="text-xs sm:text-sm px-2 py-2">
            Dnevnik
          </TabsTrigger>
          <TabsTrigger value="potrdi" className="text-xs sm:text-sm px-2 py-2">
            Čakajoči ({pendingHours.length})
          </TabsTrigger>
          <TabsTrigger value="dodaj" className="text-xs sm:text-sm px-2 py-2">
            Dodaj ure
          </TabsTrigger>
          <TabsTrigger value="pregled" className="text-xs sm:text-sm px-2 py-2">
            Pregled
          </TabsTrigger>
        </TabsList>

        {/* Dnevnik Tab */}
        <TabsContent value="dnevnik" className="mt-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <CardTitle className="text-lg">Dnevnik opravljenih ur</CardTitle>
                  <CardDescription>{canViewAllHours ? "Pregled ur vseh članov" : "Vaše opravljene ure"}</CardDescription>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {canViewAllHours && (
                    <Select value={diaryFilterUser} onValueChange={(v) => { setDiaryFilterUser(v); fetchDiary(v); }}>
                      <SelectTrigger
                        className="w-[200px] h-auto min-h-9 py-1.5 [&>span]:line-clamp-2 [&>span]:whitespace-normal [&>span]:text-left"
                        data-testid="diary-user-filter"
                      >
                        <SelectValue placeholder="Vsi člani" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Vsi člani</SelectItem>
                        {allMembers
                          .sort((a, b) => a.priimek.localeCompare(b.priimek, 'sl'))
                          .map(m => (
                          <SelectItem key={m.id} value={m.id} className="whitespace-normal break-words">{m.priimek} {m.ime}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  <div className="relative">
                    <Input
                      placeholder="Išči..."
                      value={diarySearch}
                      onChange={(e) => setDiarySearch(e.target.value)}
                      className="w-[150px]"
                    />
                  </div>
                  {canViewAllHours && (
                    <Button variant="outline" size="sm" onClick={handleExportHours} data-testid="export-hours-csv">
                      <Download className="h-4 w-4 mr-1" />
                      CSV
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {diaryEntries.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">Ni zapisov</p>
              ) : (
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {diaryEntries
                    .filter(e => {
                      if (!diarySearch) return true;
                      const s = diarySearch.toLowerCase();
                      return (e.opis || "").toLowerCase().includes(s) ||
                             (e.user_ime || "").toLowerCase().includes(s) ||
                             (e.orodje || "").toLowerCase().includes(s);
                    })
                    .map((entry) => (
                    <div key={entry.id} className="flex items-start justify-between p-3 border rounded-lg hover:bg-muted/30 transition-colors">
                      {editEntry === entry.id ? (
                        /* Edit mode */
                        <div className="flex-1 space-y-2">
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                            <div>
                              <Label className="text-xs">Datum</Label>
                              <Input type="date" value={editForm.datum} onChange={(e) => setEditForm({...editForm, datum: e.target.value})} />
                            </div>
                            <div>
                              <Label className="text-xs">Dejanske ure</Label>
                              <Input type="number" step="0.5" min="0.5" value={editForm.ure} onChange={(e) => setEditForm({...editForm, ure: parseFloat(e.target.value) || 1})} />
                            </div>
                            <div>
                              <Label className="text-xs">Orodje</Label>
                              <Select value={editForm.orodje} onValueChange={(v) => setEditForm({...editForm, orodje: v})}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {orodjaOptions.map(o => (
                                    <SelectItem key={o.value} value={o.value}>{o.label} (x{o.faktor})</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-xs">Tip</Label>
                              <Select value={editForm.tip} onValueChange={(v) => setEditForm({...editForm, tip: v})}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="delovna_akcija">Delovna akcija</SelectItem>
                                  <SelectItem value="skupni_lov">Skupni lov</SelectItem>
                                  <SelectItem value="drugo">Drugo</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <Input value={editForm.opis} onChange={(e) => setEditForm({...editForm, opis: e.target.value})} placeholder="Opis" />
                          {getOrodjeFaktor(editForm.orodje) > 1 && (
                            <p className="text-xs text-blue-700 bg-blue-50 rounded p-1">{editForm.ure}h x{getOrodjeFaktor(editForm.orodje)} = {(editForm.ure * getOrodjeFaktor(editForm.orodje)).toFixed(1)}h</p>
                          )}
                          <div className="flex gap-2">
                            <Button size="sm" onClick={handleSaveEntry}><Save className="h-3 w-3 mr-1" />Shrani</Button>
                            <Button size="sm" variant="outline" onClick={() => setEditEntry(null)}>Prekliči</Button>
                          </div>
                        </div>
                      ) : (
                        /* View mode */
                        <>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              {canViewAllHours && <span className="font-semibold text-sm break-words">{entry.user_ime}</span>}
                              {entry.podaril && entry.giver_ime && (
                                <Badge variant="outline" className="text-xs bg-pink-50 text-pink-700 border-pink-200">
                                  Podaril: {entry.giver_ime}
                                </Badge>
                              )}
                              {!entry.podaril && entry.prenos_ur && entry.vpisal_ime && entry.vpisal_id !== entry.user_id && (
                                <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                                  Vpisal: {entry.vpisal_ime}
                                </Badge>
                              )}
                              <Badge className={`${getHoursTypeColor(entry.tip)} text-xs`}>
                                {getHoursTypeLabel(entry.tip)}
                              </Badge>
                              {entry.orodje && entry.orodje !== "brez" && (
                                <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                                  {getOrodjeLabel(entry.orodje)} x{entry.faktor || 1}
                                </Badge>
                              )}
                              {entry.potrjeno ? (
                                <Badge className="bg-green-100 text-green-800 text-xs">Potrjeno</Badge>
                              ) : (
                                <Badge className="bg-amber-100 text-amber-800 text-xs">Čaka</Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">{entry.opis}</p>
                          </div>
                          <div className="flex items-start gap-2 ml-3">
                            <div className="text-right whitespace-nowrap">
                              <p className="font-bold text-lg">{entry.ure}h</p>
                              {entry.dejanske_ure && entry.dejanske_ure !== entry.ure && (
                                <p className="text-xs text-muted-foreground">{entry.dejanske_ure}h dej.</p>
                              )}
                              <p className="text-xs text-muted-foreground">{formatDate(entry.datum)}</p>
                            </div>
                            {isAdmin && (
                              <div className="flex flex-col gap-1">
                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => startEditEntry(entry)} data-testid={`edit-entry-${entry.id}`}>
                                  <Edit className="h-3.5 w-3.5" />
                                </Button>
                                <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDeleteEntry(entry.id)} data-testid={`delete-entry-${entry.id}`}>
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                  {/* Summary */}
                  <div className="border-t pt-3 mt-3 flex justify-between items-center">
                    <span className="text-sm font-medium text-muted-foreground">
                      Skupaj zapisov: {diaryEntries.length}
                    </span>
                    <span className="font-bold text-primary text-lg">
                      {diaryEntries.reduce((s, e) => s + (e.ure || 0), 0).toFixed(1)}h skupaj
                    </span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pending Approval Tab */}
        <TabsContent value="potrdi" className="mt-4">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <Card>
              <CardContent className="p-3">
                <p className="text-xs text-muted-foreground">Čakajoči</p>
                <p className="text-xl font-bold text-amber-600">{pendingHours.length}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3">
                <p className="text-xs text-muted-foreground">Članov</p>
                <p className="text-xl font-bold">{Object.keys(groupedByUser).length}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3">
                <p className="text-xs text-muted-foreground">Skupaj ur</p>
                <p className="text-xl font-bold">{pendingHours.reduce((sum, h) => sum + h.ure, 0)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3">
                <p className="text-xs text-muted-foreground">Izbranih</p>
                <p className="text-xl font-bold text-primary">{selectedIds.length}</p>
              </CardContent>
            </Card>
          </div>

          {/* Bulk Actions */}
          {pendingHours.length > 0 && (
            <Card className="bg-primary/5 border-primary/20 mb-4">
              <CardContent className="p-3">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <Checkbox 
                      checked={selectedIds.length === pendingHours.length && pendingHours.length > 0}
                      onCheckedChange={selectAll}
                      id="select-all"
                    />
                    <label htmlFor="select-all" className="text-sm cursor-pointer">
                      Izberi vse ({pendingHours.length})
                    </label>
                  </div>
                  <Button 
                    onClick={handleBulkApprove}
                    disabled={selectedIds.length === 0 || approving}
                    className="w-full sm:w-auto"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    {approving ? "Potrjujem..." : `Potrdi (${selectedIds.length})`}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Pending List */}
          {pendingHours.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <CheckCircle className="h-12 w-12 text-green-500/30 mx-auto mb-4" />
                <p className="text-muted-foreground">Ni zapisov za potrditev</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {Object.entries(groupedByUser).map(([userId, userData]) => (
                <Card key={userId}>
                  <CardHeader className="pb-2 px-4 pt-4">
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <CardTitle className="text-base flex items-start gap-2 min-w-0 flex-1">
                        <Users className="h-4 w-4 shrink-0 mt-0.5" />
                        <span className="break-words">{userData.user_ime}</span>
                      </CardTitle>
                      <Badge variant="outline" className="text-xs shrink-0">
                        {userData.entries.length} • {userData.entries.reduce((sum, e) => sum + e.ure, 0)} ur
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0 px-4 pb-4">
                    <div className="space-y-2">
                      {userData.entries.map((entry) => (
                        <div 
                          key={entry.id}
                          className={`flex items-center gap-2 p-2 rounded-lg border ${
                            selectedIds.includes(entry.id) ? 'bg-primary/5 border-primary/30' : 'bg-card'
                          }`}
                        >
                          <Checkbox 
                            checked={selectedIds.includes(entry.id)}
                            onCheckedChange={() => toggleSelect(entry.id)}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-1">
                              <Badge className={`${getHoursTypeColor(entry.tip)} text-xs`}>
                                {getHoursTypeLabel(entry.tip)}
                              </Badge>
                              {entry.orodje && entry.orodje !== "brez" && (
                                <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                                  {getOrodjeLabel(entry.orodje)} x{entry.faktor || getOrodjeFaktor(entry.orodje)}
                                </Badge>
                              )}
                              {entry.podaril && entry.giver_ime && (
                                <Badge variant="outline" className="text-xs bg-pink-50 text-pink-700 border-pink-200" title="Podarjene ure">
                                  Podaril: {entry.giver_ime}
                                </Badge>
                              )}
                              {!entry.podaril && entry.prenos_ur && entry.vpisal_ime && entry.vpisal_id !== entry.user_id && (
                                <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200" title="Prenos ur">
                                  Vpisal: {entry.vpisal_ime}
                                </Badge>
                              )}
                              <span className="font-semibold text-sm">
                                {entry.ure}h
                                {entry.dejanske_ure && entry.dejanske_ure !== entry.ure && (
                                  <span className="text-xs text-muted-foreground ml-1">({entry.dejanske_ure}h dej.)</span>
                                )}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {formatDate(entry.datum)}
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground truncate">{entry.opis}</p>
                          </div>
                          <div className="flex gap-1">
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-green-600" onClick={() => handleSingleApprove(entry.id)} title="Potrdi">
                              <Check className="h-3 w-3" />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => handleReject(entry.id)} title="Zavrni">
                              <X className="h-3 w-3" />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => handleDeleteHours(entry.id)} title="Izbriši">
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Add Hours Tab */}
        <TabsContent value="dodaj" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Dodaj ure več članom</CardTitle>
              <CardDescription>Po delovni akciji ali skupnem lovu</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Form */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Datum</Label>
                  <Input
                    type="date"
                    value={bulkForm.datum}
                    onChange={(e) => setBulkForm({ ...bulkForm, datum: e.target.value })}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Dejanske ure</Label>
                  <Input
                    type="number"
                    step="0.5"
                    min="0.5"
                    value={bulkForm.ure}
                    onChange={(e) => setBulkForm({ ...bulkForm, ure: parseFloat(e.target.value) || 1 })}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Tip</Label>
                  <Select 
                    onValueChange={(value) => setBulkForm({ ...bulkForm, tip: value })} 
                    value={bulkForm.tip}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="delovna_akcija">Delovna akcija</SelectItem>
                      <SelectItem value="skupni_lov">Skupni lov</SelectItem>
                      <SelectItem value="drugo">Drugo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Orodje</Label>
                  <Select 
                    onValueChange={(value) => setBulkForm({ ...bulkForm, orodje: value })} 
                    value={bulkForm.orodje}
                  >
                    <SelectTrigger data-testid="orodje-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {orodjaOptions.map(o => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label} (x{o.faktor})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1 col-span-2">
                  <Label className="text-xs">Opis</Label>
                  <Input
                    value={bulkForm.opis}
                    onChange={(e) => setBulkForm({ ...bulkForm, opis: e.target.value })}
                    placeholder="npr. Čiščenje gozdnih poti"
                  />
                </div>
              </div>

              {/* Factor calculation display */}
              {getOrodjeFaktor(bulkForm.orodje) > 1 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                  <p className="font-medium text-blue-800">
                    {bulkForm.ure}h x {getOrodjeFaktor(bulkForm.orodje)} ({getOrodjeLabel(bulkForm.orodje)}) = <span className="text-lg font-bold">{(bulkForm.ure * getOrodjeFaktor(bulkForm.orodje)).toFixed(1)}h</span> pomnoženih ur
                  </p>
                </div>
              )}

              {/* Member selection */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Izberite člane ({selectedMembers.length}/{allMembers.length})</Label>
                  <Button type="button" variant="link" size="sm" onClick={selectAllMembers}>
                    {selectedMembers.length === allMembers.length ? "Odznači vse" : "Izberi vse"}
                  </Button>
                </div>
                <div className="max-h-64 overflow-auto border rounded-lg p-2 grid grid-cols-1 sm:grid-cols-2 gap-1">
                  {allMembers.length === 0 ? (
                    <div className="col-span-full text-center text-sm text-muted-foreground py-6 px-2" data-testid="bulk-members-empty">
                      Seznam članov se ni naložil. Prosim, osvežite stran ali se obrnite na predsednika.
                    </div>
                  ) : (
                    allMembers.map((member) => (
                      <label 
                        key={member.id} 
                        className={`flex items-center gap-2 p-2 rounded cursor-pointer ${
                          selectedMembers.includes(member.id) ? 'bg-primary/10' : 'hover:bg-muted'
                        }`}
                      >
                        <Checkbox
                          checked={selectedMembers.includes(member.id)}
                          onCheckedChange={() => toggleMemberSelect(member.id)}
                        />
                        <span className="text-sm break-words leading-tight">{member.ime} {member.priimek}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>

              <Button 
                onClick={handleBulkAddHours}
                disabled={selectedMembers.length === 0 || !bulkForm.opis.trim()}
                className="w-full"
              >
                <Plus className="h-4 w-4 mr-2" />
                Dodaj {(bulkForm.ure * getOrodjeFaktor(bulkForm.orodje)).toFixed(1)}h za {selectedMembers.length} članov
                {getOrodjeFaktor(bulkForm.orodje) > 1 && ` (${bulkForm.ure}h x${getOrodjeFaktor(bulkForm.orodje)})`}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Overview Tab */}
        <TabsContent value="pregled" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Pregled ur članov</CardTitle>
              <CardDescription>Kliknite na svinčnik za urejanje obveznih ali opravljenih ur</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {allHoursSummary.map((member) => {
                  const obvezne = member.obvezne_ure ?? 0;
                  const opravljene = member.opravljene_ure || 0;
                  const percentage = obvezne > 0 ? Math.min(100, Math.round((opravljene / obvezne) * 100)) : (opravljene > 0 ? 100 : 0);
                  const isEditingMandatory = editingHoursId === member.id;
                  const isEditingCompleted = editCompletedHoursId === member.id;
                  
                  return (
                    <div key={member.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-muted/50">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm break-words leading-tight">{member.ime} {member.priimek}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-20 h-2 bg-muted rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${percentage >= 100 ? 'bg-green-500' : percentage >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`}
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                        
                        {/* Editing completed hours */}
                        {isEditingCompleted ? (
                          <div className="flex items-center gap-1">
                            <Input
                              type="number"
                              className="w-14 h-7 text-xs px-1"
                              value={editCompletedHoursValue}
                              onChange={(e) => setEditCompletedHoursValue(parseInt(e.target.value) || 0)}
                              min="0"
                              autoFocus
                            />
                            <span className="text-xs">/{obvezne}</span>
                            <Button 
                              size="icon" 
                              variant="ghost" 
                              className="h-7 w-7 text-green-600"
                              onClick={() => handleUpdateCompletedHours(member.id)}
                            >
                              <Save className="h-3 w-3" />
                            </Button>
                            <Button 
                              size="icon" 
                              variant="ghost" 
                              className="h-7 w-7 text-red-600"
                              onClick={() => setEditCompletedHoursId(null)}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        ) : isEditingMandatory ? (
                          <div className="flex items-center gap-1">
                            <span className="text-xs">{opravljene}/</span>
                            <Input
                              type="number"
                              className="w-14 h-7 text-xs px-1"
                              value={editHoursValue}
                              onChange={(e) => setEditHoursValue(parseInt(e.target.value) || 0)}
                              min="0"
                              autoFocus
                            />
                            <Button 
                              size="icon" 
                              variant="ghost" 
                              className="h-7 w-7 text-green-600"
                              onClick={() => handleUpdateMandatoryHours(member.id)}
                            >
                              <Save className="h-3 w-3" />
                            </Button>
                            <Button 
                              size="icon" 
                              variant="ghost" 
                              className="h-7 w-7 text-red-600"
                              onClick={() => setEditingHoursId(null)}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        ) : (
                          <>
                            <div className="flex items-center gap-1">
                              <span 
                                className="text-xs font-medium cursor-pointer hover:text-primary hover:underline"
                                onClick={() => startEditCompletedHours(member.id, opravljene)}
                                title="Uredi opravljene ure"
                              >
                                {opravljene}
                              </span>
                              <span className="text-xs text-muted-foreground">/</span>
                              <span 
                                className="text-xs font-medium cursor-pointer hover:text-primary hover:underline"
                                onClick={() => startEditHours(member.id, obvezne)}
                                title="Uredi obvezne ure"
                              >
                                {obvezne}
                              </span>
                            </div>
                            <Badge 
                              variant={percentage >= 100 ? "default" : "outline"}
                              className="w-10 justify-center text-xs"
                            >
                              {percentage}%
                            </Badge>
                            <Button 
                              size="icon" 
                              variant="ghost" 
                              className="h-7 w-7"
                              onClick={() => startEditCompletedHours(member.id, opravljene)}
                              title="Uredi opravljene ure"
                            >
                              <Edit className="h-3 w-3" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default HoursApprovalPage;
